import Game20Engine from '../Game20Engine';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCurrentAccount } from '@mysten/dapp-kit';
import SettingsModal from '../../../components/Game2048/SettingsModal';
import HamburgerButton from '../../../components/HamburgerButton';
import EnokiLoginButton from '../../../components/EnokiLoginButton';
import { getBlockchainContractConfigApiAsync } from '../../../api/api';
import Tournament from './tournament';
import Wallet from './wallet';

import '../assets/2048.css';

export default function Game2048Sui() {
    const engineRef = useRef(null);
    const [user, setUser] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showBombTip, setShowBombTip] = useState(false);
    const [showRandomTip, setShowRandomTip] = useState(false);
    const [showSkullTip, setShowSkullTip] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [dontShowRandomAgain, setDontShowRandomAgain] = useState(false);
    const [dontShowSkullAgain, setDontShowSkullAgain] = useState(false);

    // Enoki zkLogin account
    const currentAccount = useCurrentAccount();

    // React Router hooks
    const navigate = useNavigate();
    const location = useLocation();

    // 遊戲狀態管理
    const [gameMode, setGameMode] = useState('single'); // 'single' 或 'dual'
    const [showBattleSetup, setShowBattleSetup] = useState(false);
    const [showModeSelection, setShowModeSelection] = useState(false);

    // 根據當前路徑確定頁面
    const getCurrentPage = () => {
        const path = location.pathname;
        if (path.includes('/wallet')) {
            return 'wallet';
        }
        if (path.includes('/tournament')) {
            return 'tournament';
        }
        if (path.includes('/game') || path.includes('game')) {
            return 'game';
        }
        return 'home'; // 默認首頁
    };

    const currentPage = getCurrentPage();

    useEffect(() => {
        // 初始化區塊鏈合約設定
        const initializeBlockchainConfig = async () => {
            try {
                const contractConfig = await getBlockchainContractConfigApiAsync();

                // 將每個欄位分別儲存到 sessionStorage
                if (contractConfig.packageId) {
                    sessionStorage.setItem('packageId_2048', contractConfig.packageId);
                }

                if (contractConfig.uSDCCoinType) {
                    sessionStorage.setItem('blockchain_usdc_coin_type', contractConfig.treasuryCapTWENTY);
                }
                if (contractConfig.tWENTCoinType) {
                    sessionStorage.setItem('blockchain_twenty_coin_type', contractConfig.treasuryCapTWENTY);
                }
                if (contractConfig.treasuryCapTWENTY) {
                    sessionStorage.setItem('blockchain_treasury_cap_twenty', contractConfig.treasuryCapTWENTY);
                }
                if (contractConfig.treasuryCapUSDC) {
                    sessionStorage.setItem('blockchain_treasury_cap_usdc', contractConfig.treasuryCapUSDC);
                }
                if (contractConfig.sponsorAddress) {
                    sessionStorage.setItem('sponsorAddress', contractConfig.sponsorAddress);
                }
                if (contractConfig.usdcVault) {
                    sessionStorage.setItem('blockchain_usdc_vault', contractConfig.usdcVault);
                }
            } catch (error) {
                console.error('Failed to load blockchain contract config:', error);
            }
        };

        // 執行初始化
        initializeBlockchainConfig();
    }, []);

    // 同步 Enoki 帳戶到 sessionStorage 和 user state
    useEffect(() => {
        if (currentAccount) {
            const address = currentAccount.address;
            sessionStorage.setItem('zklogin_user_address', address);
            setUser({ address });
            setIsAuthenticated(true);
        } else {
            sessionStorage.removeItem('zklogin_user_address');
            setUser(null);
            setIsAuthenticated(false);
        }
    }, [currentAccount]);

    // 當進入遊戲頁面或首頁時初始化引擎
    useEffect(() => {
        let specialTilesCheckInterval = null;
        let initRetryTimer = null;
        let retryCount = 0;
        const maxRetries = 20; // 最多重試 20 次（2 秒）

        // ✅ 首頁和遊戲頁面都需要初始化引擎顯示棋盤
        if (currentPage !== 'game' && currentPage !== 'home') {
            // 不在遊戲頁面或首頁時，清理引擎
            if (initRetryTimer) clearTimeout(initRetryTimer);
            if (engineRef.current) {
                engineRef.current.destroy();
                engineRef.current = null;
                window.engine = null;
            }
            if (specialTilesCheckInterval) {
                clearInterval(specialTilesCheckInterval);
            }
            return;
        }

        // 在遊戲頁面時，初始化引擎
        const initializeEngine = () => {
            const gameBoard = document.getElementById('gameBoard');

            // ✅ 檢查引擎是否存在但 Canvas 丟失（頁面切換導致）
            if (engineRef.current && gameBoard && (!engineRef.current.canvas || !document.getElementById('gameCanvas'))) {
                try {
                    engineRef.current.initCanvas();

                    // ✅ 重新渲染棋盤
                    setTimeout(() => {
                        if (engineRef.current) {
                            engineRef.current.renderBoard();
                        }
                    }, 100);
                } catch (error) {
                    console.error('❌ Canvas 重新初始化失敗:', error);
                }
            }

            if (gameBoard && !engineRef.current) {
                try {
                    engineRef.current = new Game20Engine();
                    window.engine = engineRef.current;

                    // ✅ 檢查是否有保存的 gameId，如果有且遊戲未結束，則從服務器恢復遊戲狀態
                    const savedGameId = sessionStorage.getItem('gameId');
                    if (savedGameId && currentPage === 'game') {

                        // 等待 SignalR 連接建立後再恢復狀態
                        const tryRestoreGameState = async () => {
                            if (engineRef.current && engineRef.current.signalRConnection) {
                                const restored = await engineRef.current.restoreGameStateFromServer(savedGameId);
                            } else {
                                // 如果 SignalR 還沒連接，等待一下再試
                                setTimeout(tryRestoreGameState, 500);
                            }
                        };

                        // 延遲一下，確保引擎和 SignalR 都初始化完成
                        setTimeout(tryRestoreGameState, 1000);
                    }

                    // 監聽特殊方塊出現事件
                    const checkForSpecialTiles = () => {
                        checkForBombTiles();
                        checkForRandomTiles();
                        checkForSkullTiles();
                    };

                    // 定期檢查特殊方塊
                    specialTilesCheckInterval = setInterval(checkForSpecialTiles, 2000);

                } catch (error) {
                    console.error('❌ 遊戲引擎初始化失敗:', error);
                }
            } else if (!gameBoard && retryCount < maxRetries) {
                retryCount++;
                initRetryTimer = setTimeout(initializeEngine, 100);
            } else if (!gameBoard) {
            }
        };

        // 延遲初始化，等待 DOM 準備就緒
        initRetryTimer = setTimeout(() => {
            initializeEngine();
        }, 100);

        return () => {
            // 只清理 timer 和 interval
            if (initRetryTimer) clearTimeout(initRetryTimer);
            if (specialTilesCheckInterval) {
                clearInterval(specialTilesCheckInterval);
            }
            // ⚠️ 不在這裡銷毀引擎，因為引擎需要在 home 和 game 頁面之間保持
            // 引擎只在上面的條件判斷中銷毀（當離開 home/game 頁面時）
        };
    }, [currentPage]);

    const handleNewGame = () => {
        if (engineRef.current) engineRef.current.newGame();
    };
    const handleUndo = () => {
        if (engineRef.current) engineRef.current.undoMove();
    };

    const handleEndGame = () => {
        navigate('/2048sui');
    };

    // 檢查炸彈方塊的函數
    const checkForBombTiles = () => {
        const bombTipShown = localStorage.getItem('bombTipShown');
        if (bombTipShown !== 'true' && engineRef.current) {
            // 檢查棋盤上是否有炸彈方塊
            const hasBombTiles = engineRef.current.bombBoard.some(row =>
                row.some(cell => cell === true)
            );

            if (hasBombTiles && !showBombTip) {
                setShowBombTip(true);
            }
        }
    };

    // 檢查亂數方塊的函數
    const checkForRandomTiles = () => {
        const randomTipShown = localStorage.getItem('randomTipShown');
        if (randomTipShown !== 'true' && engineRef.current) {
            // 檢查棋盤上是否有亂數方塊
            const hasRandomTiles = engineRef.current.randomBoard.some(row =>
                row.some(cell => cell === true)
            );

            if (hasRandomTiles && !showRandomTip) {
                setShowRandomTip(true);
            }
        }
    };

    // 檢查愛心方塊的函數
    const checkForSkullTiles = () => {
        const skullTipShown = localStorage.getItem('skullTipShown');
        if (skullTipShown !== 'true' && engineRef.current) {
            // 檢查棋盤上是否有愛心方塊
            const hasSkullTiles = engineRef.current.skullBoard.some(row =>
                row.some(cell => cell === true)
            );

            if (hasSkullTiles && !showSkullTip) {
                setShowSkullTip(true);
            }
        }
    };



    // 模式選擇處理函數
    const handleSinglePlayerMode = () => {
        // ✅ 清除舊的 gameId，確保創建新遊戲
        sessionStorage.removeItem('gameId');
        sessionStorage.removeItem('multiplayer_game_id');

        setGameMode('single');
        setShowModeSelection(false); // 關閉模式選擇 modal
        navigate('/2048sui/game?mode=single');

        // 等待 DOM 渲染和引擎初始化完成後再開始新遊戲
        setTimeout(() => {
            let retryCount = 0;
            const maxRetries = 50; // 增加到 50 次（5 秒）

            const startNewGame = () => {
                const gameBoard = document.getElementById('gameBoard');

                // ✅ 確保 gameBoard、引擎和 Canvas 都已初始化
                if (gameBoard && engineRef.current && engineRef.current.canvas && engineRef.current.ctx) {
                    try {
                        engineRef.current.newGame();
                    } catch (error) {
                        console.error('❌ 啟動新遊戲失敗:', error);
                    }
                } else if (retryCount < maxRetries) {
                    retryCount++;
                    // 如果條件不滿足，繼續等待
                    setTimeout(startNewGame, 100);
                }
            };

            startNewGame();
        }, 300);
    };

    const handleBombTipConfirm = () => {
        // if (dontShowAgain) {
        localStorage.setItem('bombTipShown', 'true');
        // }
        setShowBombTip(false);
        setDontShowAgain(true);
    };

    const handleRandomTipConfirm = () => {
        // if (dontShowRandomAgain) {
        localStorage.setItem('randomTipShown', 'true');
        // }
        setShowRandomTip(false);
        setDontShowRandomAgain(true);
    };

    const handleSkullTipConfirm = () => {
        // if (dontShowSkullAgain) {
        localStorage.setItem('skullTipShown', 'true');
        // }
        setShowSkullTip(false);
        setDontShowSkullAgain(true);
    };


    // 鍵盤支持 - ESC 關閉 modal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isMenuOpen]);

    // 如果是錢包頁面，直接渲染 Wallet 組件
    if (currentPage === 'wallet') {
        return <Wallet />;
    }

    // 如果是錦標賽頁面，直接渲染 Tournament 組件
    if (currentPage === 'tournament') {
        return <Tournament />;
    }

    return (
        <>
            <div className="game-wrapper">
                {/* Home Page - 顯示棋盤背景和透明Modal */}
                {currentPage === 'home' && (
                    <>
                        {/* 棋盤背景 */}
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.25,
                            pointerEvents: 'none'
                        }}>
                            <div className="game-container" style={{
                                width: '100%',
                                maxWidth: '500px',
                                padding: '20px'
                            }}>
                                <div className="game-board" id="gameBoard">
                                    {/* Cells are rendered by the engine */}
                                </div>
                            </div>
                        </div>

                        {/* 透明Modal覆蓋層 */}
                        <div className="home-modal-overlay">
                            <div className="home-modal-content">
                                <div className="home-modal-items">
                                    {/* 標題 */}
                                    <div className="home-modal-title">🎮 Welcome to 2048!</div>
                                    <div className="home-modal-subtitle">
                                        Connect your wallet and start playing
                                    </div>

                                    {/* Google Login 區域 */}
                                    <div className="home-modal-item">
                                        <div className="home-modal-item-title">🔐 Login</div>
                                        {currentAccount ? (
                                            <>
                                                <div className="home-modal-wallet-info">
                                                    <div className="home-modal-wallet-address">
                                                        {currentAccount.address.slice(0, 6)}...{currentAccount.address.slice(-4)}
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: '12px' }}>
                                                    <EnokiLoginButton />
                                                </div>
                                            </>
                                        ) : (
                                            <EnokiLoginButton />
                                        )}
                                    </div>

                                    {/* 開始遊戲按鈕 */}
                                    <div className="home-modal-item">
                                        <div className="home-modal-item-title">🎯 Start Game</div>
                                        <button
                                            className="home-modal-btn start-btn"
                                            onClick={handleSinglePlayerMode}
                                        >
                                            Start Game
                                        </button>
                                    </div>

                                    {/* USDC 獎金賽按鈕 */}
                                    {/* <div className="home-modal-item">
                                        <div className="home-modal-item-title">💰 USDC 獎金賽</div>
                                        <div className="home-modal-item-desc">
                                            Join tournaments and win USDC prizes!
                                        </div>
                                        <button
                                            className="home-modal-btn tournament-btn"
                                            onClick={() => {
                                                // 導航到獎金賽頁面
                                                navigate('/2048sui/tournament');
                                            }}
                                        >
                                            🏆 Join Tournament
                                        </button>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Connecting Modal */}
                <div className="connecting-modal" id="connectingModal">
                    <div className="connecting-content">
                        <div className="connecting-spinner"></div>
                        <div className="connecting-title">遊戲連線中</div>
                        <div className="connecting-status">正在連線中...</div>
                        <div className="connecting-progress">
                            <div className="progress-bar">
                                <div className="progress-fill"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bomb Tip Modal */}
                <div className={`bomb-tip-modal ${showBombTip ? 'show' : ''}`}>
                    <div className="bomb-tip-content">
                        <div className="bomb-tip-title">💣 Bomb tile</div>
                        <div className="bomb-tip-description">
                            When two same number bomb tiles merge, it will explode!
                        </div>
                        {/* <div className="bomb-tip-example">
                            <div className="bomb-tip-example-title">💥 爆炸效果說明：</div>
                            <div className="bomb-tip-example-text">
                                • 當兩個相同數字的炸彈方塊合併時<br/>
                                • 會產生十字形的爆炸<br/>
                                • 爆炸會清除周圍的方塊<br/>
                                • 爆炸範圍為十字形，影響上下左右四個方向
                            </div>
                        </div> */}
                        {/* <div className="bomb-tip-checkbox">
                            <input 
                                type="checkbox" 
                                id="dontShowAgain"
                                checked={dontShowAgain}
                                onChange={(e) => setDontShowAgain(e.target.checked)}
                            />
                            <label htmlFor="dontShowAgain">不再提醒</label>
                        </div> */}
                        <button className="bomb-tip-button" onClick={handleBombTipConfirm}>
                            Got it
                        </button>
                    </div>
                </div>

                {/* Random Tip Modal */}
                <div className={`random-tip-modal ${showRandomTip ? 'show' : ''}`}>
                    <div className="random-tip-content">
                        <div className="random-tip-title">🎲 Random Tile</div>
                        <div className="random-tip-description">
                            You need to click it twice to reveal its value!
                        </div>
                        {/* <div className="random-tip-example">
                            <div className="random-tip-example-title">🎯 使用方法：</div>
                            <div className="random-tip-example-text">
                                • 亂數方塊顯示為 "？" 符號<br/>
                                • 點擊亂數方塊可以確認其數值<br/>
                                • 確認後的數值會從 1, 2, 4, 8, 16 中隨機選擇<br/>
                                • 確認前無法與其他方塊合併<br/>
                                • 確認後可以正常參與遊戲
                            </div>
                        </div> */}
                        {/* <div className="random-tip-checkbox">
                            <input 
                                type="checkbox" 
                                id="dontShowRandomAgain"
                                checked={dontShowRandomAgain}
                                onChange={(e) => setDontShowRandomAgain(e.target.checked)}
                            />
                            <label htmlFor="dontShowRandomAgain">不再提醒</label>
                        </div> */}
                        <button className="random-tip-button" onClick={handleRandomTipConfirm}>
                            Got it
                        </button>
                    </div>
                </div>

                {/* Skull Tip Modal */}
                <div className={`skull-tip-modal ${showSkullTip ? 'show' : ''}`}>
                    <div className="skull-tip-content">
                        <div className="skull-tip-title">💀 Skull tile</div>
                        <div className="skull-tip-description">
                            Two skulls collide and disappear, if it is bombed, it will end the game!
                        </div>
                        {/* <div className="skull-tip-example">
                            <div className="skull-tip-example-title">⚠️ 重要提醒：</div>
                            <div className="skull-tip-example-text">
                                • 愛心方塊顯示為 "❤️" 符號<br/>
                                • 愛心方塊不能被合併<br/>
                                • 愛心方塊不能被移動<br/>
                                • 如果愛心方塊被炸彈炸到，遊戲立即結束<br/>
                                • 愛心方塊是遊戲的保護目標
                            </div>
                        </div> */}
                        {/* <div className="skull-tip-checkbox">
                            <input 
                                type="checkbox" 
                                id="dontShowSkullAgain"
                                checked={dontShowSkullAgain}
                                onChange={(e) => setDontShowSkullAgain(e.target.checked)}
                            />
                            <label htmlFor="dontShowSkullAgain">不再提醒</label>
                        </div> */}
                        <button className="skull-tip-button" onClick={handleSkullTipConfirm}>
                            Got it
                        </button>
                    </div>
                </div>

                {/* Hamburger Menu - 隱藏在首頁 */}
                {currentPage !== 'home' && (
                    <div className="menu-section">
                        <HamburgerButton
                            isOpen={isMenuOpen}
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        />
                    </div>
                )}

                {/* Settings Modal */}
                <SettingsModal
                    isOpen={isMenuOpen}
                    onClose={() => setIsMenuOpen(false)}
                    showBackButton={false}
                />

                {/* New 2048 Layout */}
                <div className="game-header">
                    <h1 className="game-title">2048</h1>
                    <div className="scores-container">
                        <div className="info-item">
                            <span className="info-label">SCORE</span>
                            <span className="info-value" id="score">0</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">BEST</span>
                            <span className="info-value" id="bestScore">0</span>
                        </div>
                    </div>
                </div>

                {currentPage === 'game' && (
                    <div className="above-game">
                        {/* <p className="game-intro">Join the numbers and get to the <strong>20 tile!</strong></p> */}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {/* <button className="control-btn" onClick={handleUndo} id="undoBtn">Undo</button> */}
                                <button className="control-btn" onClick={handleNewGame}>New Game</button>
                                <button className="control-btn" onClick={handleEndGame}>End Game</button>
                            </div>
                            <div className="special-tiles-info">
                                <div className="info-btn-wrapper">
                                    <div className="info-badge">i</div>
                                    <button className="info-btn bomb-info-btn" onClick={() => setShowBombTip(true)} title="炸彈方塊說明">
                                        💣
                                    </button>
                                </div>
                                <div className="info-btn-wrapper">
                                    <div className="info-badge">i</div>
                                    <button className="info-btn skull-info-btn" onClick={() => setShowSkullTip(true)} title="骷髏頭方塊說明">
                                        💀
                                    </button>
                                </div>
                                <div className="info-btn-wrapper">
                                    <div className="info-badge">i</div>
                                    <button className="info-btn random-info-btn" onClick={() => setShowRandomTip(true)} title="亂數方塊說明">
                                        🎲
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {currentPage === 'game' && (
                    <div className="game-container">
                        <div className="game-board" id="gameBoard">
                        </div>
                    </div>
                )}

                {/* Game Over Modal (Preserved Structure, but will be styled by new CSS) */}
                <div className="game-over" id="gameOver">
                    <div className="game-over-content" id="gameOverContent">
                        <h2 id="gameOverTitle">Game Over!</h2>
                        <p id="gameOverMessage">Try again!</p>
                        <button className="restart-btn" onClick={handleNewGame}>Try again</button>
                    </div>
                </div>
            </div>

            {/* 首頁透明Modal樣式 */}
            <style>{`
                /* 首頁透明Modal覆蓋層 */
                .home-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    background: rgba(0, 0, 0, 0.3);
                    backdrop-filter: blur(2px);
                }
                
                /* 首頁Modal內容 */
                .home-modal-content {
                    background: rgba(250, 248, 239, 0.95);
                    border-radius: 12px;
                    padding: 32px;
                    min-width: 320px;
                    max-width: 420px;
                    width: 90%;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }
                
                .home-modal-items {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                
                .home-modal-title {
                    font-size: 1.8em;
                    font-weight: 700;
                    color: #776e65;
                    text-align: center;
                    margin-bottom: 8px;
                }
                
                .home-modal-subtitle {
                    font-size: 0.95em;
                    color: #776e65;
                    text-align: center;
                    opacity: 0.8;
                    margin-bottom: 20px;
                }
                
                .home-modal-item {
                    background: rgba(238, 228, 218, 0.9);
                    border-radius: 8px;
                    padding: 18px 20px;
                    backdrop-filter: blur(10px);
                }
                
                .home-modal-item-title {
                    font-weight: 600;
                    margin-bottom: 12px;
                    font-size: 1em;
                    color: #776e65;
                }
                
                .home-modal-wallet-info {
                    margin-top: 8px;
                }
                
                .home-modal-wallet-address {
                    font-family: monospace;
                    font-size: 0.9em;
                    color: #776e65;
                    background: rgba(255, 255, 255, 0.5);
                    padding: 8px 12px;
                    border-radius: 6px;
                    text-align: center;
                }
                
                .home-modal-btn {
                    background: #8f7a66;
                    color: #f9f6f2;
                    border: none;
                    border-radius: 6px;
                    padding: 12px 20px;
                    width: 100%;
                    cursor: pointer;
                    text-align: center;
                    font-size: 1em;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }
                
                .home-modal-btn:hover:not(:disabled) {
                    background: #9f8b77;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                }
                
                .home-modal-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .home-modal-btn.start-btn {
                    background: #4caf50;
                }
                
                .home-modal-btn.start-btn:hover:not(:disabled) {
                    background: #45a049;
                }
                
                .home-modal-btn.connect-btn {
                    background: #2196F3;
                }
                
                .home-modal-btn.connect-btn:hover:not(:disabled) {
                    background: #1976D2;
                }
                
                .home-modal-btn.tournament-btn {
                    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
                    color: #333;
                    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
                }
                
                .home-modal-btn.tournament-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #FFA500 0%, #FF8C00 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
                }
                
                .home-modal-btn.wallet-btn {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
                }
                
                .home-modal-btn.wallet-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
                }
                
                .home-modal-item-desc {
                    font-size: 0.85em;
                    opacity: 0.8;
                    margin-bottom: 10px;
                    color: #776e65;
                }
                
                /* 統一 Google 登入按鈕樣式 */
                .home-modal-item .enoki-login-btn {
                    background: #8f7a66 !important;
                    color: #f9f6f2 !important;
                    border: none !important;
                    border-radius: 6px !important;
                    padding: 12px 20px !important;
                    box-shadow: none !important;
                    font-weight: 600 !important;
                }
                
                .home-modal-item .enoki-login-btn:hover {
                    background: #9f8b77 !important;
                    transform: translateY(-2px) !important;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
                }
                
                .home-modal-item .enoki-login-btn.logged-in {
                    background: #6c757d !important;
                }
                
                .home-modal-item .enoki-login-btn.logged-in:hover {
                    background: #5a6268 !important;
                }
                
                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid #f9f6f2;
                    border-top: 2px solid transparent;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    display: inline-block;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                /* 響應式設計 */
                @media (max-width: 768px) {
                    .home-modal-content {
                        padding: 24px;
                        min-width: 280px;
                    }
                    
                    .home-modal-title {
                        font-size: 1.5em;
                    }
                    
                    .home-modal-item {
                        padding: 14px 16px;
                    }
                }
            `}</style>

        </>
    );
}